/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactNode } from 'react';
import { AuthTabs } from './auth-tabs';
import { GuestOrderLookupPanel } from './guest-order-lookup-panel';

export interface AuthPageLayoutProps {
    activeTab: 'login' | 'signup';
    children: ReactNode;
}

/**
 * Two-column shell shared by /login and /signup, rendered inside the standard header/footer
 * layout: left column is the tab-switched Login / Create Account form, right column is the
 * guest order lookup — check an order or start a return without an account.
 */
export function AuthPageLayout({ activeTab, children }: AuthPageLayoutProps) {
    return (
        <div data-section="auth" className="section-container py-12">
            <div className="mx-auto max-w-5xl grid gap-16 md:grid-cols-2">
                <div>
                    <AuthTabs activeTab={activeTab} />
                    <div className="mt-8">{children}</div>
                </div>
                <GuestOrderLookupPanel />
            </div>
        </div>
    );
}
